import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import CanadaTrqPanel, {
  TrqPanelQuota,
  TrqPosition,
} from './CanadaTrqPanel';

type QuestionOption = {
  id: string;
  label: string;
  enablesExclusions: string[];
};

type ExclusionQuestion = {
  id: string;
  prompt: string;
  helperText: string;
  options: QuestionOption[];
};

// A question whose answer changes which measures MATCH, rather than which
// exclusion codes are enabled. Used by the Canada tab, where the answer is a
// request parameter and each change re-runs the lookup.
type ScopeQuestion = {
  id: string;
  prompt: string;
  helperText: string;
  options: Array<{ id: string; label: string }>;
  selected?: string;
};

type RiskFlag = {
  id: string;
  title: string;
  description: string;
  severity: 'high' | 'medium' | 'info';
};

type HtsHistoryItem = {
  id: string;
  date: string;
  summary: string;
};

interface TariffToolInsightsPanelProps {
  htsHistory?: HtsHistoryItem[];
  riskFlags?: RiskFlag[];
  hts_code?: string;
  origin_country?: string;
  // New Props
  questions?: ExclusionQuestion[];
  enabledExclusions?: Record<string, boolean>;
  onAnswer?: (codesToEnable: string[], codesToDisable: string[]) => void;
  // Scope questions are kept separate from the exclusion questions above:
  // those toggle exclusion codes locally, these are answered back to the
  // server. The card is omitted entirely when there are none.
  scopeQuestions?: ScopeQuestion[];
  onScopeAnswer?: (questionId: string, optionId: string) => void;
  scopeTitle?: string;
  scopeBusy?: boolean;
  // Compact summary only — the conditions themselves run to paragraphs
  // (one remission summary is ~1,500 characters), which a third-width grid
  // column cannot hold, so the detail lives behind `onViewRelief`.
  reliefMeasures?: Array<{ id: string; measure: string; count: number }>;
  onViewRelief?: () => void;
  // Canada tariff-rate quotas. When present these take the first card slot,
  // replacing the Exclusion Assistant — that card is driven by US Section 232
  // exclusion codes and has nothing to say on the Canada tab, while the quota
  // declaration is the most consequential input on the page.
  trqQuotas?: TrqPanelQuota[];
  trqPositions?: Record<string, TrqPosition>;
  onDeclareTrq?: (iclItem: string, position: TrqPosition) => void;
  onChooseTrqVariant?: (
    iclItem: string,
    side: TrqPosition,
    index: number,
  ) => void;
  trqVariantChoice?: Record<string, number>;
  onViewTrqDetails?: () => void;
  trqBusy?: boolean;
  // Which tariff tab this is. Canada shows the quota card in place of the
  // Exclusion Applicability Assistant, which is driven by US Section 232
  // exclusion codes and has nothing to say on any other tab.
  tariffCountry?: 'US' | 'MX' | 'CA';
  showScreeningLink?: boolean;
  // Screening-link target. Defaults reproduce the original US behavior (US
  // Import tab, AD/CVD + Forced Labor + PGA lenses). Canada (and future
  // regions) override these to open the matching import tab and prefill the
  // list/lens that surfaced their flags.
  screeningTradeType?: string;
  screeningLenses?: string[];
  screeningLists?: string[];
}

const TariffToolInsightsPanel: React.FC<TariffToolInsightsPanelProps> = ({
  htsHistory = [],
  riskFlags = [],
  hts_code,
  origin_country,
  questions = [],
  enabledExclusions = {},
  onAnswer,
  scopeQuestions = [],
  onScopeAnswer,
  reliefMeasures = [],
  onViewRelief,
  trqQuotas = [],
  trqPositions = {},
  onDeclareTrq,
  onChooseTrqVariant,
  trqVariantChoice = {},
  onViewTrqDetails,
  trqBusy = false,
  tariffCountry = 'US',
  scopeTitle = 'Measure Applicability',
  scopeBusy = false,
  showScreeningLink = true,
  screeningTradeType = 'US Import',
  screeningLenses = [
    'AD CVD Commodities',
    'Forced Labor Commodities',
    'PGA Codes',
  ],
  screeningLists = [],
}) => {
  // Calculate which answers are currently selected based on the parent's enabledExclusions map
  const selectedAnswers = useMemo(() => {
    const state: Record<string, string> = {};

    questions.forEach((q) => {
      // 1. Identify the universe of codes relevant to this specific question
      const allCodesForQuestion = new Set(
        q.options.flatMap((o) => o.enablesExclusions),
      );

      // 2. Identify which of those codes are currently ACTIVE in the parent state
      const activeCodes = new Set(
        [...allCodesForQuestion].filter((code) => enabledExclusions[code]),
      );

      // 3. Find the option that EXACTLY matches the set of active codes
      // This handles:
      // - Empty sets (e.g., Heavy Duty): activeCodes is empty, option codes are empty -> Match
      // - Subsets (e.g., Auto Part vs Neither): Auto Part matches only if A is on. Neither matches only if A + B are on.
      const exactMatch = q.options.find((opt) => {
        const optCodes = opt.enablesExclusions;
        if (optCodes.length !== activeCodes.size) return false;
        return optCodes.every((code) => activeCodes.has(code));
      });

      if (exactMatch) {
        state[q.id] = exactMatch.id;
      }
    });

    return state;
  }, [questions, enabledExclusions]);

  const derivedExclusions = useMemo(() => {
    // Just for display text at bottom of card
    return questions.reduce<string[]>((acc, question) => {
      const selectedOptionId = selectedAnswers[question.id];
      const selectedOption = question.options.find(
        (option) => option.id === selectedOptionId,
      );
      if (!selectedOption) return acc;
      return [...acc, ...selectedOption.enablesExclusions];
    }, []);
  }, [questions, selectedAnswers]);

  const handleOptionClick = (
    question: ExclusionQuestion,
    option: QuestionOption,
  ) => {
    if (!onAnswer) return;

    const toEnable = option.enablesExclusions;
    const toEnableSet = new Set(toEnable);

    // Find all codes associated with *other* options for this question
    // STRICT FILTER: Do not disable a code that we are trying to enable.
    // (This fixes potential race conditions if "Neither" enables A+B but "Auto" disables A)
    const toDisable = question.options
      .filter((o) => o.id !== option.id)
      .flatMap((o) => o.enablesExclusions)
      .filter((code) => !toEnableSet.has(code));

    onAnswer(toEnable, toDisable);
  };

  // Adaptive layout, applied only where a quota card is present.
  //
  // Empty cards are dropped rather than rendered as "nothing to report", and
  // the quota card takes the freed column — it is the densest card and the
  // one carrying the decision. It falls back to a single column once two or
  // more of the others have real content, so nothing is ever pushed onto a
  // second row.
  const isCanada = tariffCountry === 'CA';
  // Canada always carries the quota card, empty or not — it takes the
  // Exclusion Assistant's slot rather than sitting alongside it.
  const showQuotaCard = isCanada || trqQuotas.length > 0;
  const hasTrq = trqQuotas.length > 0;
  const othersWithContent = hasTrq
    ? [
        scopeQuestions.length > 0,
        reliefMeasures.length > 0,
        riskFlags.length > 0,
        htsHistory.length > 0,
      ].filter(Boolean).length
    : -1;
  const trqSpansTwo = hasTrq && othersWithContent <= 1;
  // Only suppressed alongside a quota card; every other tab keeps the
  // "no flags found" / "no changes detected" reassurance.
  const showRiskFlags = !hasTrq || riskFlags.length > 0;
  const showHtsHistory = !hasTrq || htsHistory.length > 0;

  return (
    <div className='tariff-tool-insights-root'>
      {/* 0. QUOTA DECLARATION — leads the panel. One column wide, so it sits
          on the same row as Risk Flags and HTS Code History rather than
          pushing one of them below. */}
      {showQuotaCard ? (
        <CanadaTrqPanel
          wide={trqSpansTwo}
          quotas={trqQuotas}
          positions={trqPositions}
          onDeclare={onDeclareTrq}
          onChooseVariant={onChooseTrqVariant}
          variantChoice={trqVariantChoice}
          onViewDetails={onViewTrqDetails}
          disabled={trqBusy}
        />
      ) : null}

      {/* 0a. MEASURE APPLICABILITY CARD — only rendered when a matched measure
          actually turns on one of these questions. */}
      {scopeQuestions.length > 0 ? (
        <div className='tariff-tool-insights-card'>
          <div className='tariff-tool-insights-card-header'>
            <div className='screening-ai-label-text'>{scopeTitle}</div>
          </div>

          <div className='tariff-desc-text'>
            These answers change which measures apply, so the tariff is
            recalculated each time one changes.
          </div>

          {scopeQuestions.map((question) => (
            <div
              key={question.id}
              className='tariff-tool-insights-question-group'
            >
              <div className='tariff-desc-text'>{question.helperText}</div>
              <div className='screening-ai-label-text'>{question.prompt}</div>

              <div
                className='tariff-tool-insights-options-row'
                style={{
                  display: 'flex',
                  gap: '10px',
                  marginTop: '8px',
                  flexDirection: 'column',
                }}
              >
                {question.options.map((option) => {
                  const isSelected = question.selected === option.id;
                  return (
                    <div
                      key={option.id}
                      className={`tariff-tool-insights-option ${
                        isSelected ? 'is-active' : ''
                      }`}
                      onClick={() =>
                        scopeBusy ? undefined : onScopeAnswer?.(question.id, option.id)
                      }
                      style={{
                        flex: 1,
                        cursor: scopeBusy ? 'not-allowed' : 'pointer',
                        opacity: scopeBusy ? 0.6 : 1,
                      }}
                    >
                      <div className='tariff-tool-insights-option-radio' />
                      <div className='screening-ai-label-text'>
                        {option.label}
                      </div>
                    </div>
                  );
                })}
              </div>

              {question.selected === undefined ? (
                <div className='tariff-desc-text' style={{ marginTop: '6px' }}>
                  Unanswered — the measure is shown as conditional until this is
                  confirmed.
                </div>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}

      {/* 0b. RELIEF & EXEMPTIONS CARD */}
      {reliefMeasures.length > 0 ? (
        <div className='tariff-tool-insights-card'>
          <div className='tariff-tool-insights-card-header'>
            <div className='screening-ai-label-text'>Relief &amp; Exemptions</div>
          </div>

          <div className='tariff-desc-text'>
            The duty above assumes none of these apply. Each depends on
            something outside this line — the shipment total, the transit date,
            or a claim the importer has to make — so check them before relying
            on the figure.
          </div>

          {reliefMeasures.map((m) => (
            <div key={m.id} className='tariff-tool-insights-question-group'>
              <div className='screening-ai-label-text'>{m.measure}</div>
              <div className='tariff-desc-text'>
                {m.count} condition{m.count === 1 ? '' : 's'} to check
              </div>
            </div>
          ))}

          <button
            type='button'
            className='btn-outline'
            onClick={() => onViewRelief?.()}
            style={{ marginTop: '4px' }}
          >
            View conditions
          </button>
        </div>
      ) : null}

      {/* 1. EXCLUSION ASSISTANT — never shown where the quota card is, which
          on the Canada tab is always. The two never both belong: the
          assistant is driven by US Section 232 exclusion codes, so it has
          nothing to say outside the US tab. */}
      {showQuotaCard ? null : (
      <div className='tariff-tool-insights-card'>
        <div className='tariff-tool-insights-card-header'>
          <div className='screening-ai-label-text'>
            Exclusion Applicability Assistant
          </div>
        </div>

        {questions.length > 0 ? (
          <>
            {questions.map((question) => (
              <div
                key={question.id}
                className='tariff-tool-insights-question-group'
              >
                <div className='tariff-desc-text'>{question.helperText}</div>
                <div className='screening-ai-label-text'>{question.prompt}</div>

                <div
                  className='tariff-tool-insights-options-row'
                  style={{
                    display: 'flex',
                    gap: '10px',
                    marginTop: '8px',
                    flexDirection: 'column',
                  }}
                >
                  {question.options.map((option) => {
                    const isSelected =
                      selectedAnswers[question.id] === option.id;
                    return (
                      <div
                        key={option.id}
                        className={`tariff-tool-insights-option ${
                          isSelected ? 'is-active' : ''
                        }`}
                        onClick={() => handleOptionClick(question, option)}
                        style={{ flex: 1, cursor: 'pointer' }}
                      >
                        <div className='tariff-tool-insights-option-radio' />
                        <div className='screening-ai-label-text'>
                          {option.label}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            <div className='tariff-desc-text' style={{ marginTop: '15px' }}>
              Enabled codes:{' '}
              {derivedExclusions.length > 0
                ? derivedExclusions.join(', ')
                : 'None'}
            </div>
          </>
        ) : (
          <div className='tariff-desc-text'>
            No exclusions applicable for assistance found for this transaction.
          </div>
        )}
      </div>
      )}

      {/* 2. RISK FLAGS CARD */}
      {showRiskFlags && (
      <div className='tariff-tool-insights-card'>
        <div className='screening-ai-label-text'>Risk Flags</div>

        {riskFlags.length === 0 ? (
          <div className='tariff-desc-text'>
            {isCanada
              ? 'No flags found for SIMA measures or PGA requirements.'
              : 'No flags found for AD/CVD, Forced Labor, or PGA.'}
          </div>
        ) : (
          <>
            {riskFlags.map((flag) => (
              <div
                key={flag.id}
                className={`tariff-tool-insights-flag tariff-tool-insights-flag-${flag.severity}`}
              >
                <div className='tariff-tool-insights-flag-header'>
                  <div className='screening-ai-label-text'>{flag.title}</div>
                  <div className='tariff-tool-insights-flag-dot' />
                </div>
                <div className='tariff-desc-text'>{flag.description}</div>
              </div>
            ))}

            {showScreeningLink && (
              <div className='tariff-tool-insights-screening-row'>
                <div className='tariff-desc-text'>
                  Detailed screening analysis is available with the Screening
                  module.
                </div>
                <Link
                  className='tariff-classify-link'
                  to={{
                    pathname: '/import-screening',
                    state: {
                      primary: hts_code,
                      country: origin_country,
                      tradeType: screeningTradeType,
                      lenses: screeningLenses,
                      lists: screeningLists,
                    },
                  }}
                >
                  <h4>Go to Screening →</h4>
                </Link>
              </div>
            )}
          </>
        )}
      </div>

      )}

      {/* 3. HTS HISTORY CARD */}
      {showHtsHistory && (
      <div className='tariff-tool-insights-card'>
        <div className='screening-ai-label-text'>HTS Code History</div>
        <div className='tariff-desc-text'>
          {htsHistory.length > 0
            ? 'Recent and upcoming changes to this HTS classification.'
            : 'No recent changes detected for this HTS code.'}
        </div>

        {htsHistory.map((item) => (
          <div key={item.id} className='tariff-tool-insights-history-item'>
            <div className='screening-ai-label-text'>{item.date}</div>
            <div className='tariff-desc-text'>{item.summary}</div>
          </div>
        ))}
      </div>
      )}
    </div>
  );
};

export default TariffToolInsightsPanel;
